import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Globe, Search, MapPin, Home, Check, X, Stethoscope, Building2,
  CalendarDays, Crown, BookOpen,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import WorldMap, { NAZVY_OBSAHU } from '../components/world/WorldMap';
import Flag from '../components/world/Flag';
import HomeCountryPicker from '../components/world/HomeCountryPicker';
import ContentRow from '../components/world/ContentRow';
import CountryPreview from '../components/world/CountryPreview';
import { useJazyk } from '../context/LanguageContext';
import { API } from '../lib/api';
import { plural } from '../lib/utils';

/**
 * Bloom World – rozcestník po zemích.
 *
 * Mapa je hlavní způsob, jak se sem vstupuje, ale nikdy ne jediný: pod ní je
 * hledání a úplný seznam zemí, který funguje i bez myši a bez načtené mapy.
 */

// Kategorie obsahu. Přibývají, jak se přidávají další části Bloom World –
// schválně tu nejsou prázdné položky, které by nic nedělaly.
const KATEGORIE = [
  { key: 'all' },
  { key: 'specialists' },
  { key: 'organizations' },
  { key: 'events' },
  { key: 'pageants' },
  { key: 'resources' },
];

const SVETADILY = [
  { key: '' },
  { key: 'europe' },
  { key: 'asia' },
  { key: 'africa' },
  { key: 'north_america' },
  { key: 'south_america' },
  { key: 'oceania' },
];

const bezDiakritiky = (t) =>
  (t || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export default function WorldPage() {
  const navigate = useNavigate();
  const { t, dvojjazycne } = useJazyk();
  const [zeme, setZeme] = useState([]);
  const [domov, setDomov] = useState(null);
  const [nacita, setNacita] = useState(true);
  const [chyba, setChyba] = useState('');
  // Země vybraná na mapě. Ukazuje se v náhledu pod mapou, aby člověk při
  // zkoumání nepřišel o mapu po každém kliknutí.
  const [vybrana, setVybrana] = useState(null);
  const [nacitaVybranou, setNacitaVybranou] = useState(false);
  const nahledRef = useRef(null);
  const [hledani, setHledani] = useState('');
  const [kategorie, setKategorie] = useState('all');
  const [svetadil, setSvetadil] = useState('');
  const [ukladamDomov, setUkladamDomov] = useState(false);
  const [uvod, setUvod] = useState(null);
  const [picketSkryt, setPicketSkryt] = useState(
    () => localStorage.getItem('bloom_home_country_skipped') === '1'
  );

  useEffect(() => {
    let zivy = true;
    (async () => {
      try {
        const [mapa, dom, souhrn] = await Promise.all([
          axios.get(`${API}/geo/world-map`),
          axios.get(`${API}/geo/home-country`),
          axios.get(`${API}/geo/home-summary`).catch(() => ({ data: null })),
        ]);
        if (!zivy) return;
        setZeme(mapa.data);
        setDomov(dom.data.country);
        setUvod(souhrn.data);
      } catch {
        if (zivy) setChyba('svet.chyba');
      } finally {
        if (zivy) setNacita(false);
      }
    })();
    return () => { zivy = false; };
  }, []);

  const otevriZemi = (z) => navigate(`/world/${z.slug}`);

  /**
   * Klik do mapy nenačte jinou stránku, ale rozbalí náhled pod mapou.
   *
   * Původně to rovnou přeskakovalo na stránku země. Kdo si mapu prohlíží,
   * ale nechce nikam odejít – chce vědět, co v té zemi je, a podívat se hned
   * na další. Odchod na stránku země má v náhledu vlastní tlačítko.
   */
  const ukazZemi = async (z) => {
    setNacitaVybranou(true);
    setVybrana({ ...z });   // hlavička se ukáže hned, podrobnosti dotéčou
    try {
      const { data } = await axios.get(`${API}/geo/countries/${z.slug}`);
      setVybrana(data);
    } catch {
      setVybrana(z);        // aspoň to, co už máme z mapy
    } finally {
      setNacitaVybranou(false);
      // Na mobilu je náhled pod ohybem – bez posunutí by to vypadalo,
      // že klik nic neudělal.
      requestAnimationFrame(() => {
        nahledRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    }
  };

  // Dokud si uživatel nevybral domovskou zemi, klik na mapu ji rovnou nastaví –
  // je to přesně ta otázka, na kterou se ptáme kousek pod mapou. Jakmile ji má,
  // klik zase normálně otevře zemi.
  const vybiraDomov = !domov && !picketSkryt;
  const klikNaMapu = (z) => (vybiraDomov ? nastavDomov(z.iso2) : ukazZemi(z));

  const nastavDomov = async (iso2) => {
    setUkladamDomov(true);
    try {
      await axios.put(`${API}/geo/home-country`, { country_iso2: iso2 });
      const [dom, souhrn] = await Promise.all([
        axios.get(`${API}/geo/home-country`),
        axios.get(`${API}/geo/home-summary`).catch(() => ({ data: null })),
      ]);
      setDomov(dom.data.country);
      setUvod(souhrn.data);
    } finally {
      setUkladamDomov(false);
    }
  };

  // Seznam pod mapou – přístupná cesta ke stejnému obsahu.
  const seznam = useMemo(() => {
    const h = bezDiakritiky(hledani);
    return zeme
      .filter((z) => (svetadil ? z.continent === svetadil : true))
      .filter((z) => (kategorie === 'all' ? true : (z.counts?.[kategorie] || 0) > 0))
      .filter((z) => !h
        || bezDiakritiky(z.name_cs).includes(h)
        || bezDiakritiky(z.name_en).includes(h)
        || z.iso2.toLowerCase() === h)
      .sort((a, b) => (b.total - a.total) || a.name_cs.localeCompare(b.name_cs, 'cs'));
  }, [zeme, hledani, kategorie, svetadil]);

  const sObsahem = useMemo(() => zeme.filter((z) => z.total > 0), [zeme]);

  // Vyhledávací tabulka podle kódu – karty obsahu ze světa potřebují vlajku
  // a slug země, a nemá smysl je pokaždé hledat v poli.
  const podleKodu = useMemo(
    () => Object.fromEntries(zeme.map((z) => [z.iso2, z])),
    [zeme]
  );

  const preskocitDomov = () => {
    localStorage.setItem('bloom_home_country_skipped', '1');
    setPicketSkryt(true);
  };

  const uvodniSekce = [
    { key: 'specialists', icon: Stethoscope },
    { key: 'organizations', icon: Building2 },
    { key: 'events', icon: CalendarDays },
    { key: 'pageants', icon: Crown },
    { key: 'resources', icon: BookOpen },
  ];

  if (nacita) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center"><div className="spinner" /></div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8" data-testid="world-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-bloom-text flex items-center gap-2">
            <Globe className="w-7 h-7 text-bloom-violet" />
            {t('svet.titulek')}
          </h1>
          <p className="mt-1.5 text-bloom-sub text-sm sm:text-base">
            {vybiraDomov
              ? t('svet.zacniDomovem')
              : t('svet.zkoumej')}
          </p>
        </div>
        {domov && (
          <div className="flex items-center gap-2 bg-bloom-mint/15 border border-bloom-mint/40 rounded-2xl px-3.5 py-2">
            <Home className="w-4 h-4 text-bloom-violet" />
            <Flag emoji={domov.flag} iso2={domov.iso2} size="sm" />
            <span className="text-sm text-bloom-text">
              {t('svet.domov')}: <strong>{dvojjazycne(domov.name_cs, domov.name_en)}</strong>
            </span>
            <button type="button" onClick={() => nastavDomov(null)} disabled={ukladamDomov}
                    className="text-bloom-sub hover:text-bloom-text" aria-label={t('svet.zrusitDomov')}>
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </header>

      {chyba && (
        <p className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{t(chyba)}</p>
      )}

      {/* Filtry podle druhu obsahu */}
      <div className="mt-6 flex flex-wrap gap-2" role="tablist" aria-label="Druh obsahu">
        {KATEGORIE.map((k) => (
          <button
            key={k.key}
            role="tab"
            aria-selected={kategorie === k.key}
            onClick={() => setKategorie(k.key)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
              kategorie === k.key
                ? 'bg-bloom-violet text-white shadow-sm'
                : 'bg-white border border-border/50 text-bloom-sub hover:text-bloom-text'
            }`}
            data-testid={`world-cat-${k.key}`}
          >
            {k.key === 'all' ? t('svet.vse') : t(`sekce.${k.key}`)}
          </button>
        ))}
      </div>

      {/* Mapa */}
      <div className="mt-4 rounded-3xl overflow-hidden border border-border/40 bg-white shadow-sm">
        <WorldMap
          countries={zeme}
          homeCountryIso2={domov?.iso2 || null}
          onSelectCountry={klikNaMapu}
          activeCategory={kategorie}
          className="w-full aspect-[2/1] sm:aspect-[2.2/1]"
        />
      </div>

      <p className="mt-2 text-xs text-bloom-sub/70">
        {t('svet.napovedaKMape')}
      </p>

      {/* Vybraná země – hned pod mapou, ne místo ní. */}
      <div ref={nahledRef}>
        <CountryPreview
          zeme={vybrana}
          nacita={nacitaVybranou && !vybrana?.counts}
          jeDomov={domov?.iso2 === vybrana?.iso2}
          onZavrit={() => setVybrana(null)}
          onNastavitDomov={nastavDomov}
        />
      </div>

      {/* Výběr domovské země – jen když ji uživatel nemá a nepřeskočil to */}
      {!domov && !picketSkryt && (
        <div className="mt-8">
          <HomeCountryPicker zeme={zeme} uklada={ukladamDomov}
                             onVyber={nastavDomov} onPreskocit={preskocitDomov} />
        </div>
      )}

      {/* Tvůj Bloom v domovské zemi */}
      {domov && uvod?.home_sections && Object.keys(uvod.home_sections).length > 0 && (
        <section className="mt-9" data-testid="home-sections">
          <div className="flex items-center gap-2">
            <Flag emoji={domov.flag} iso2={domov.iso2} size="md" />
            <h2 className="font-heading text-lg sm:text-xl font-bold text-bloom-text">
              {t('svet.tvujBloom', { zeme: dvojjazycne(domov.name_cs, domov.name_en) })}
            </h2>
          </div>
          {uvodniSekce.map((s) => (
            <ContentRow key={s.key} icon={s.icon} title={t(`sekce.${s.key}`)} kind={s.key}
                        polozky={uvod.home_sections[s.key] || []}
                        zeme={podleKodu}
                        odkazVice={`/world/${domov.slug}`} />
          ))}
        </section>
      )}

      {/* Ze světa */}
      {uvod?.around_world && Object.values(uvod.around_world).some((v) => v.length > 0) && (
        <section className="mt-10" data-testid="around-world">
          <h2 className="font-heading text-lg sm:text-xl font-bold text-bloom-text flex items-center gap-2">
            <Globe className="w-5 h-5 text-bloom-violet" />
            {t('svet.zeSveta')}
          </h2>
          <ContentRow icon={CalendarDays} title={t('svet.coSeChysta')} kind="events"
                      polozky={uvod.around_world.events} zeme={podleKodu} ukazatZemi />
          <ContentRow icon={Crown} title={t('svet.souteze')} kind="pageants"
                      polozky={uvod.around_world.pageants} zeme={podleKodu} ukazatZemi />
          <ContentRow icon={Building2} title={t('svet.noveOrganizace')} kind="organizations"
                      polozky={uvod.around_world.organizations} zeme={podleKodu} ukazatZemi />
        </section>
      )}

      {/* Kde Bloom něco má */}
      {sObsahem.length > 0 && (
        <section className="mt-8">
          <h2 className="font-heading text-lg font-bold text-bloom-text">{t('svet.kdeJizNeco')}</h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {sObsahem.slice(0, 6).map((z) => (
              <KartaZeme key={z.iso2} zeme={z} onOpen={() => otevriZemi(z)}
                         domaci={domov?.iso2 === z.iso2} />
            ))}
          </div>
        </section>
      )}

      {/* Hledání a úplný seznam */}
      <section className="mt-10">
        <h2 className="font-heading text-lg font-bold text-bloom-text">{t('svet.vsechnyZeme')}</h2>

        <div className="mt-3 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-bloom-sub" />
            <Input
              value={hledani}
              onChange={(e) => setHledani(e.target.value)}
              placeholder={t('svet.hledatZemi')}
              className="pl-9"
              aria-label={t('svet.hledatZemiPopisek')}
              data-testid="world-country-search"
            />
          </div>
          <select
            value={svetadil}
            onChange={(e) => setSvetadil(e.target.value)}
            aria-label={t('svet.kontinent')}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm text-bloom-text"
            data-testid="world-continent-filter"
          >
            {SVETADILY.map((sv) => (
              <option key={sv.key} value={sv.key}>
                {sv.key ? t(`kontinent.${sv.key}`) : t('svet.celySvet')}
              </option>
            ))}
          </select>
        </div>

        <p className="mt-3 text-sm text-bloom-sub">
          {t('svet.zemi', { pocet: seznam.length })}
        </p>

        {seznam.length === 0 ? (
          <p className="mt-4 text-bloom-sub">{t('svet.nicNeodpovida')}</p>
        ) : (
          <ul className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="world-country-list">
            {seznam.map((z) => (
              <li key={z.iso2}>
                <button
                  type="button"
                  onClick={() => otevriZemi(z)}
                  className="w-full flex items-center gap-3 text-left bg-white border border-border/40 rounded-xl px-3.5 py-2.5 hover:border-bloom-violet/40 hover:shadow-sm transition-all"
                >
                  <Flag emoji={z.flag} iso2={z.iso2} size="md" />
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium text-bloom-text truncate">
                      {dvojjazycne(z.name_cs, z.name_en)}
                    </span>
                    <span className="block text-xs text-bloom-sub">
                      {z.total > 0 ? popisPoctu(z.counts, t) : t('zeme.zatimPrazdne')}
                    </span>
                  </span>
                  {domov?.iso2 !== z.iso2 && (
                    <span
                      role="button"
                      tabIndex={0}
                      title={t('svet.nastavitDomov')}
                      onClick={(e) => { e.stopPropagation(); nastavDomov(z.iso2); }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); nastavDomov(z.iso2); }
                      }}
                      className="shrink-0 text-bloom-sub/50 hover:text-bloom-violet"
                    >
                      <Home className="w-4 h-4" />
                    </span>
                  )}
                  {domov?.iso2 === z.iso2 && <Check className="w-4 h-4 text-bloom-mint shrink-0" />}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/** „3 organizace · 1 akce" – v jazyce, který má člověk zvolený. */
function popisPoctu(counts = {}, t) {
  return Object.entries(counts)
    .filter(([, n]) => n > 0)
    .map(([klic, pocet]) => t(`svet.pocet.${klic}`, { pocet }))
    .join(' · ');
}

function KartaZeme({ zeme, onOpen, domaci }) {
  const { t, dvojjazycne } = useJazyk();
  return (
    <button
      type="button"
      onClick={onOpen}
      className="text-left bg-white border border-border/40 rounded-2xl p-4 hover:border-bloom-violet/40 hover:shadow-sm transition-all"
      data-testid={`world-card-${zeme.iso2}`}
    >
      <div className="flex items-center gap-2">
        <Flag emoji={zeme.flag} iso2={zeme.iso2} size="lg" />
        <div className="min-w-0">
          <p className="font-heading font-bold text-bloom-text truncate">
            {dvojjazycne(zeme.name_cs, zeme.name_en)}
          </p>
          {domaci && <p className="text-[11px] text-bloom-violet">{t('svet.tvojeDomovska')}</p>}
        </div>
      </div>
      <p className="mt-2.5 text-sm text-bloom-sub flex items-center gap-1.5">
        <MapPin className="w-3.5 h-3.5" />
        {popisPoctu(zeme.counts, t)}
      </p>
    </button>
  );
}
